import Papa from 'papaparse';
import * as XLSX from 'xlsx';

export interface ContactSubmission {
  id: number;
  name: string;
  email: string;
  phone: string;
  apartmentType: string;
  inquiryType: string;
  message: string;
  visitDate?: string;
  status: string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

/**
 * Format submission data for export
 */
function formatSubmissionForExport(submission: ContactSubmission) {
  return {
    ID: submission.id,
    Name: submission.name,
    Email: submission.email,
    Phone: submission.phone,
    'Apartment Type': submission.apartmentType,
    'Inquiry Type': submission.inquiryType,
    'Visit Date': submission.visitDate || 'N/A',
    Status: submission.status,
    Message: submission.message,
    'Submitted Date': new Date(submission.createdAt).toLocaleString(),
    'Updated Date': new Date(submission.updatedAt).toLocaleString(),
  };
}

/**
 * Export submissions as CSV
 */
export function exportToCSV(submissions: ContactSubmission[], filename = 'contact-submissions.csv') {
  const formattedData = submissions.map(formatSubmissionForExport);
  
  const csv = Papa.unparse(formattedData);
  
  // Create blob and download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Export submissions as Excel (.xlsx)
 */
export function exportToExcel(submissions: ContactSubmission[], filename = 'contact-submissions.xlsx') {
  const formattedData = submissions.map(formatSubmissionForExport);
  
  // Create workbook and worksheet
  const ws = XLSX.utils.json_to_sheet(formattedData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Submissions');
  
  // Set column widths for better readability
  const columnWidths = [
    { wch: 8 },   // ID
    { wch: 20 },  // Name
    { wch: 25 },  // Email
    { wch: 15 },  // Phone
    { wch: 15 },  // Apartment Type
    { wch: 15 },  // Inquiry Type
    { wch: 15 },  // Visit Date
    { wch: 12 },  // Status
    { wch: 40 },  // Message
    { wch: 20 },  // Submitted Date
    { wch: 20 },  // Updated Date
  ];
  ws['!cols'] = columnWidths;
  
  // Write file
  XLSX.writeFile(wb, filename);
}

/**
 * Export submissions as both CSV and Excel in a zip file
 */
export async function exportBoth(submissions: ContactSubmission[], baseFilename = 'contact-submissions') {
  // For now, we'll export both separately
  // In a production app, you might want to create a zip file
  
  const timestamp = new Date().toISOString().split('T')[0];
  
  exportToCSV(submissions, `${baseFilename}-${timestamp}.csv`);
  
  // Small delay to avoid browser blocking
  await new Promise(resolve => setTimeout(resolve, 500));
  
  exportToExcel(submissions, `${baseFilename}-${timestamp}.xlsx`);
}

/**
 * Get summary statistics for submissions
 */
export function getSubmissionStats(submissions: ContactSubmission[]) {
  const stats = {
    total: submissions.length,
    byStatus: {} as Record<string, number>,
    byInquiryType: {} as Record<string, number>,
    byApartmentType: {} as Record<string, number>,
  };
  
  submissions.forEach(sub => {
    // Count by status
    stats.byStatus[sub.status] = (stats.byStatus[sub.status] || 0) + 1;
    
    // Count by inquiry type
    stats.byInquiryType[sub.inquiryType] = (stats.byInquiryType[sub.inquiryType] || 0) + 1;
    
    // Count by apartment type
    stats.byApartmentType[sub.apartmentType] = (stats.byApartmentType[sub.apartmentType] || 0) + 1;
  });
  
  return stats;
}
